/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {
  ApplicationFeatures,
  CRUDPermissions,
  DownloadJobType
} from '@ion/core/src/types/entities';
import {StopActionRowButton} from '@ion/dls/src/components/buttons/StopActionRowButton';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {cancelTasks} from '../../../store/actions/jobsearchengine';

// defines the jobs 2.0 cancel task prop definitions ...
export interface JobSearchEngineCancelTaskTypes {
  job?: GridResult;
  task?: GridResult;
  hasTask?: boolean;
  jobsPermissions?: CRUDPermissions;
  Features?: ApplicationFeatures;
}

export const isTaskCancelDisabled = (progressStatus: string): boolean => ([
  DownloadJobType.Completed,
  DownloadJobType.Failed,
  DownloadJobType.Cancelled,
  DownloadJobType.Cancelling
].includes(progressStatus));

export const isTaskPending = (progressStatus: string): boolean => ([
  DownloadJobType.Pending
].includes(progressStatus));

/**
 * Defines the JobSearchEngineCancelTask component. This component contains the actions
 * to cancel tasks level job.
 *
 * ```typescript
 *
 *   import {JobSearchEngineCancelTask} from './JobSearchEngineCancelTask';
 *
 *   <JobSearchEngineCancelTask .../>
 *
 * ```
 */
export const JobSearchEngineCancelTask = ({
  job,
  task,
  Features,
  jobsPermissions
}: JobSearchEngineCancelTaskTypes) => {
  const {progressStatus} = task || {};
  const dispatch         = useDispatch();
  const taskDisabled     = isTaskCancelDisabled(progressStatus);
  const isPending        = isTaskPending(progressStatus);

  const {
    hasDeletePermission
  } = jobsPermissions || {};

  const cancelTask = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!isPending) {
      dispatch(cancelTasks(job));
    }
  }, [isPending, job]);

  const handleTooltipCaption = useCallback(() => {
    if (isPending) {
      return translate('jobsearchengine:cancelPendingJobTooltip');
    }
    return translate('common:tooltips.cancelEntity', {entity: translate('common:entityLabels.task')});
  }, [isPending]);

  return (
    <StopActionRowButton onClick={cancelTask}
      tooltipCaption={handleTooltipCaption()}
      isDisabled={taskDisabled || !hasDeletePermission}
      isPending={isPending} />
  );
};
